import React from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../../store";
import { captureSnapshot } from "../../../store/storyline/actions";
import { buildSnapshotData, formatSnapshotSize, getSerializedSizeBytes, serializeSnapshotData } from "../../../store/storyline/snapshots";
import { SharedWithAutocomplete } from "./SharedWithAutocomplete";
import { Alert as AlertIcon } from "../../../shared/components/icons";

export interface CreateSnapshotDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export function CreateSnapshotDialog(props: CreateSnapshotDialogProps) {
    const { open, setOpen } = props;
    const dispatch = useThunkDispatch();
    const storylineState = useSelector((state: RootState) => state.storyline);
    const [name, setName] = React.useState("");
    const [sharedWith, setSharedWith] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);

    // Serializing the captured state can touch megabytes of frame data - only do it while the dialog is open...
    const sizeBytes = React.useMemo(
        () => open ? getSerializedSizeBytes(serializeSnapshotData(buildSnapshotData(storylineState))) : 0,
        [open, storylineState]);

    const close = () => {
        if (!isSaving) setOpen(false);
    };

    const create = async () => {
        setIsSaving(true);
        const result = await dispatch(captureSnapshot({
            name: name.trim() || undefined,
            sharedWith
        }));
        setIsSaving(false);

        if (result) {
            setOpen(false);
            setName("");
            setSharedWith([]);
        }
    };

    return (
        <Dialog id="create-snapshot-dialog" open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>Create snapshot</DialogTitle>
            <DialogContent className="d-flex flex-column">
                <Alert color="info" icon={false} className="margin-bottom-2">
                    This captures and stores the full state of the current page for archival and sharing purposes.  Snapshot size: <strong>{formatSnapshotSize(sizeBytes)}</strong>
                    <br /><br />
                    <strong>NOTE: Only yourself and any users explicitly listed in the "Share with" field will be able to access this snapshot.</strong>
                </Alert>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Name (optional)"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={name}
                    onChange={event => setName(event.target.value)}
                />
                <SharedWithAutocomplete value={sharedWith} onChange={setSharedWith} />
                {
                    sharedWith && sharedWith.length > 0 &&
                    <Alert color="error" icon={<AlertIcon />}>
                        These users will be able to view the same data you are currently seeing on this page.  Please double-check that no sensitive business information is inadvertently being shared with the wrong people.
                    </Alert>
                }

            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={isSaving}>Cancel</Button>
                <Button onClick={create} disabled={isSaving} variant="contained" color="primary">
                    Create + copy link
                </Button>
            </DialogActions>
        </Dialog>
    );
}
